// Activity forms of an outlet call: opens right after the order is placed (or when a call is closed
// without an order) so the form is not forgotten. Mandatory forms (set by the admin per form)
// must be filled before the call can be completed.
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ClipboardEdit, Loader2, Lock, Store } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import OrderShareButtons from "@/components/orders/OrderShareButtons";
import { readPendingClose, clearPendingClose } from "@/lib/callForms";

export default function CallForms() {
  const { visit_id } = useParams();
  const nav = useNavigate();
  const { state } = useLocation();
  const [data, setData] = useState(null);       // {items, has_order, pending_required, visit_status}
  const [visit, setVisit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, v] = await Promise.all([
        api.get(`/activity-forms/for-visit/${visit_id}`),
        api.get(`/visits/${visit_id}`),
      ]);
      setData(f.data);
      setVisit(v.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setData({ items: [], has_order: false, pending_required: [], visit_status: null });
    }
  }, [visit_id]);
  useEffect(() => { load(); }, [load]);

  const retailer = visit?.retailer || {};
  const retailerId = visit?.retailer_id;
  const inProgress = (data?.visit_status || visit?.status) === "in_progress";
  const pending = data?.pending_required || [];
  const requiredKey = data?.has_order ? "required_with_order" : "required_without_order";
  const returnTo = `/field/call-forms/${visit_id}`;

  const fill = (f) => nav(`/forms/${f.id}/fill?visit_id=${visit_id}&retailer_id=${retailerId || ""}&return=${encodeURIComponent(returnTo)}`);

  // A call closed without an order remembers the reason the user already chose.
  const savedClose = !data?.has_order ? readPendingClose(visit_id) : null;

  const completeCall = async () => {
    setBusy(true);
    try {
      const gps = await new Promise((res) => {
        if (!navigator.geolocation) return res({ lat: 0, lng: 0 });
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: Math.round(p.coords.accuracy || 0) }),
          () => res({ lat: 0, lng: 0 }), { timeout: 4000 });
      });
      const r = await offlineMutate({
        method: "POST", path: `/visits/${visit_id}/end`, entity_type: "visits",
        body: {
          gps, outcome: "productive", ...(savedClose || {}),
          client_ts: new Date().toISOString(),
          idempotency_key: (crypto.randomUUID?.() || `call-end-${visit_id}-${Date.now()}`),
        },
      });
      clearPendingClose(visit_id);
      toast.success(r.immediate ? "Call completed" : "Queued — will sync when online");
      nav("/today-beat", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>;

  return (
    <div className="p-4 pb-28 space-y-4" data-testid="call-forms-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Activity form</div>
        <h1 className="text-xl font-extrabold text-neutral-900">{retailer.name || "Outlet call"}</h1>
        {retailer.code && <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><Store className="h-3 w-3" /> {retailer.code}</div>}
      </div>

      {state?.orderNo && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Order <b>{state.orderNo}</b> booked ✓ — now fill the activity form{pending.length ? " to complete the call" : ""}.
        </div>
      )}

      {data.order && data.items.some((f) => f.done) && (
        <Card className="border-emerald-200 bg-emerald-50 rounded-2xl" data-testid="call-forms-share">
          <CardContent className="p-3 space-y-2">
            <div className="text-sm text-emerald-900 font-semibold">
              Activity form saved ✓ — share order {data.order.order_no}
            </div>
            <div className="text-[11px] text-emerald-800">The PDF and email include the activity form details.</div>
            <OrderShareButtons order={data.order} />
          </CardContent>
        </Card>
      )}

      {data.items.length === 0 ? (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-6 text-center text-sm text-neutral-500">No activity form for this outlet.</CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.items.map((f) => {
            const required = f[requiredKey];
            return (
              <Card key={f.id} className={"rounded-2xl " + (required && !f.done ? "border-amber-300 bg-amber-50/50" : "border-neutral-200")}
                    data-testid={`call-form-${f.id.slice(0, 8)}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-neutral-900 truncate">{f.name}</div>
                    {f.description && <div className="text-[11px] text-neutral-500 line-clamp-2">{f.description}</div>}
                    <div className="mt-1 flex items-center gap-1.5 text-[10px]">
                      {f.done
                        ? <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 font-semibold">Filled ✓</span>
                        : required
                          ? <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Mandatory</span>
                          : <span className="rounded-full bg-neutral-100 text-neutral-600 px-2 py-0.5 font-semibold">Optional</span>}
                      <span className="text-neutral-400">{f.field_count} questions</span>
                    </div>
                  </div>
                  {!f.done && (
                    <Button size="sm" onClick={() => fill(f)} className="tulsi-gradient text-white h-9 rounded-lg" data-testid={`call-form-fill-${f.id.slice(0, 8)}`}>
                      <ClipboardEdit className="h-4 w-4 mr-1" /> Fill
                    </Button>
                  )}
                  {f.done && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="fixed bottom-16 inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[480px] z-20 bg-white/95 backdrop-blur border-t border-neutral-200 p-3 space-y-1.5">
        {inProgress && pending.length > 0 && (
          <div className="text-[11px] text-amber-700 text-center">
            Fill the mandatory form{pending.length === 1 ? "" : "s"} to complete this call.
          </div>
        )}
        {inProgress && (data.has_order || savedClose) ? (
          <Button onClick={completeCall} disabled={busy || pending.length > 0}
                  className="w-full h-12 rounded-xl tulsi-gradient text-white font-bold" data-testid="call-complete-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Complete call
          </Button>
        ) : inProgress ? (
          <Button onClick={() => nav(`/field/retailer/${retailerId}/dashboard?visit_id=${visit_id}&close=1`, { replace: true })}
                  disabled={pending.length > 0}
                  className="w-full h-12 rounded-xl tulsi-gradient text-white font-bold" data-testid="call-continue-btn">
            Continue — close the call
          </Button>
        ) : (
          <Button onClick={() => nav("/today-beat", { replace: true })} variant="outline" className="w-full h-12 rounded-xl" data-testid="call-done-btn">
            Done — back to Today's Beat
          </Button>
        )}
      </div>
    </div>
  );
}
